import { FormEvent, useEffect, useMemo, useState } from "react";
import { Loader2, Mail, ShieldAlert } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { SwiftLogo } from "@/components/brand/swift-logo";
import { ThemeToggle } from "@/components/theme/theme-toggle";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, isAdmin } from "@/lib/auth";

function sanitizeCallback(raw: string | null): string {
  if (!raw) return "/admin";
  const value = raw.trim();
  if (!value.startsWith("/admin")) return "/admin";
  if (value.startsWith("//")) return "/admin";
  if (value.startsWith("/admin/signin")) return "/admin";
  return value;
}

export function AdminSignInPage() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const { session, status, signOut } = useAuth();

  const callbackPath = useMemo(
    () => sanitizeCallback(search.get("callbackUrl")),
    [search],
  );
  const callbackUrl = useMemo(() => {
    if (typeof window === "undefined") return callbackPath;
    return new URL(callbackPath, window.location.origin).toString();
  }, [callbackPath]);

  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (status === "authenticated" && isAdmin(session)) {
      navigate(callbackPath, { replace: true });
    }
  }, [callbackPath, navigate, session, status]);

  const sendMagicLink = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setError("Enter your admin email.");
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const adminAllowedRes = await fetch("/api/auth/admin-allowed", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed }),
      });

      if (!adminAllowedRes.ok) {
        throw new Error("Unable to validate admin access.");
      }

      const adminAllowed = (await adminAllowedRes.json()) as { allowed?: boolean };
      if (!adminAllowed.allowed) {
        setError("This email is not an admin account.");
        return;
      }

      // So backend signIn callback can enforce ADMIN_EMAILS / ADMIN role.
      document.cookie = "admin_intent=1; path=/; max-age=3600; SameSite=Lax";

      const csrfRes = await fetch("/api/auth/csrf", { credentials: "include" });
      if (!csrfRes.ok) throw new Error("Unable to load auth token.");
      const csrfJson = (await csrfRes.json()) as { csrfToken?: string };
      if (!csrfJson.csrfToken) throw new Error("Missing auth token.");

      const body = new URLSearchParams({
        csrfToken: csrfJson.csrfToken,
        email: trimmed,
        callbackUrl,
        json: "true",
      });

      const signInRes = await fetch("/api/auth/signin", {
        method: "POST",
        credentials: "include",
        redirect: "manual",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      // Match customer-app NextAuth behaviour (manual redirect so we don't silently
      // follow a cross-origin 302 away from this SPA — see SignInForm).
      if (signInRes.ok || signInRes.status === 0) {
        try {
          const json = (await signInRes.json()) as { url?: string };
          if (json.url) {
            window.location.href = json.url;
            return;
          }
        } catch {
          // Non-JSON success body — fall through to "check email".
        }
        setSent(true);
        return;
      }

      throw new Error("Unable to send sign-in email.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign in failed. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void sendMagicLink();
  };

  if (status === "loading") {
    return (
      <main className="brand-bg flex min-h-screen items-center justify-center p-6">
        <div className="flex items-center gap-2.5 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" aria-hidden />
          Checking session…
        </div>
      </main>
    );
  }

  if (status === "authenticated" && session && !isAdmin(session)) {
    return (
      <main className="brand-bg flex min-h-screen items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-2">
            <SwiftLogo className="w-[130px] text-foreground" />
            <CardTitle className="flex items-center gap-2">
              <ShieldAlert className="size-4" aria-hidden />
              Admin access required
            </CardTitle>
            <CardDescription>
              You are signed in, but this account does not have admin access.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              type="button"
              onClick={() => void signOut({ callbackUrl: "/admin/signin" })}
              className="w-full"
            >
              Sign out and try another account
            </Button>
          </CardContent>
        </Card>
      </main>
    );
  }

  if (sent) {
    return (
      <main className="brand-bg relative flex min-h-screen flex-col items-center justify-center px-6 py-14">
        <div className="absolute right-6 top-6">
          <ThemeToggle />
        </div>

        <div className="w-full max-w-[420px]">
          <div className="space-y-6 rounded-3xl border border-border bg-card p-8 text-center brand-shadow-lg">
            <div className="flex justify-center">
              <SwiftLogo className="h-7 w-auto text-foreground dark:invert" />
            </div>
            <span
              className="mx-auto flex size-12 items-center justify-center rounded-2xl bg-muted text-foreground ring-1 ring-inset ring-border"
              aria-hidden
            >
              <Mail className="size-5" />
            </span>
            <div className="space-y-1.5">
              <p className="brand-eyebrow">Almost there</p>
              <h1 className="brand-heading brand-heading-md">Check your inbox</h1>
            </div>
            <p className="brand-body">
              We sent a secure admin sign-in link to <strong>{email}</strong>.
              Open that email on this device and continue.
            </p>
            <p className="text-[11.5px] text-muted-foreground">
              Can&apos;t find it? Check your spam folder or wait a few seconds.
            </p>

            <div className="space-y-2.5 pt-1">
              <Button
                type="button"
                className="w-full"
                disabled={submitting}
                onClick={() => void sendMagicLink()}
              >
                {submitting ? (
                  <>
                    <Loader2 className="size-4 animate-spin" aria-hidden />
                    Sending link…
                  </>
                ) : (
                  "Resend sign-in link"
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                disabled={submitting}
                onClick={() => {
                  setSent(false);
                  setError(null);
                }}
              >
                Use another email
              </Button>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="brand-bg relative flex min-h-screen items-center justify-center p-6">
      <div className="absolute right-6 top-6">
        <ThemeToggle />
      </div>
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-2">
          <SwiftLogo className="w-[130px] text-foreground" />
          <CardTitle>Admin sign in</CardTitle>
          <CardDescription>
            {sent
              ? "Check your inbox for the secure sign-in link."
              : "Use your admin email to continue to the admin dashboard."}
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="admin-email">Admin email</Label>
              <Input
                id="admin-email"
                type="email"
                autoComplete="email"
                placeholder="name@company.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                disabled={submitting}
                required
              />
            </div>

            {error ? (
              <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </p>
            ) : null}

            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                  Sending link…
                </>
              ) : sent ? (
                "Resend sign-in link"
              ) : (
                "Send sign-in link"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </main>
  );
}
